import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTenantId } from './useTenantId';

export interface TaskSuggestion {
  id: string;
  user_id: string;
  email_message_id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  due_time: string | null;
  priority: 'critical' | 'high' | 'medium' | 'low';
  urgency_score: number | null;
  sentiment: string | null;
  assigned_to_name: string | null;
  assigned_to_email: string | null;
  tags: string[] | null;
  estimated_hours: number | null;
  confidence_score: number;
  task_signature: string | null;
  extraction_context: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'auto_approved';
  reviewed_at: string | null;
  created_task_id: string | null;
  created_at: string;
  updated_at: string;
}

export function useTaskSuggestions() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantId();

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['task-suggestions', 'pending'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_suggestions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TaskSuggestion[];
    },
  });

  const { data: suggestionsCount = 0 } = useQuery({
    queryKey: ['task-suggestions-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('task_suggestions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 30000, // Refresh every 30s
  });

  const approveSuggestions = useMutation({
    mutationFn: async (suggestionIds: string[]) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const createdTasks: string[] = [];

      for (const id of suggestionIds) {
        const suggestion = suggestions.find(s => s.id === id);
        if (!suggestion) continue;

        // Create task
        const { data: newTask, error: taskError } = await supabase
          .from('tasks')
          .insert({
            user_id: user.id,
            tenant_id: tenantId,
            title: suggestion.title,
            description: suggestion.description,
            due_date: suggestion.due_date,
            due_time: suggestion.due_time,
            priority: suggestion.priority,
            urgency_score: suggestion.urgency_score,
            sentiment: suggestion.sentiment,
            assigned_to_name: suggestion.assigned_to_name,
            assigned_to_email: suggestion.assigned_to_email,
            tags: suggestion.tags,
            estimated_hours: suggestion.estimated_hours,
            task_signature: suggestion.task_signature,
            created_by_ai: true,
          })
          .select()
          .single();

        if (taskError) {
          console.error('Error creating task:', taskError);
          continue;
        }

        // Update suggestion status
        await supabase
          .from('task_suggestions')
          .update({
            status: 'approved',
            reviewed_at: new Date().toISOString(),
            created_task_id: newTask.id,
          })
          .eq('id', id);

        // Link email to task
        if (newTask) {
          await supabase
            .from('task_email_links')
            .insert({
              task_id: newTask.id,
              email_message_id: suggestion.email_message_id,
              user_id: user.id,
              tenant_id: tenantId,
              is_source_email: true,
            });
          
          createdTasks.push(newTask.id);
        }
      }

      return createdTasks;
    },
    onSuccess: (_, suggestionIds) => {
      queryClient.invalidateQueries({ queryKey: ['task-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({
        title: '✅ Tasks created',
        description: `Created ${suggestionIds.length} task${suggestionIds.length !== 1 ? 's' : ''}`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error creating tasks',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const rejectSuggestions = useMutation({
    mutationFn: async (suggestionIds: string[]) => {
      const { error } = await supabase
        .from('task_suggestions')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString(),
        })
        .in('id', suggestionIds);

      if (error) throw error;
    },
    onSuccess: (_, suggestionIds) => {
      queryClient.invalidateQueries({ queryKey: ['task-suggestions'] });
      toast({
        title: 'Suggestions dismissed',
        description: `Dismissed ${suggestionIds.length} suggestion${suggestionIds.length !== 1 ? 's' : ''}`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error dismissing suggestions',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    suggestions,
    suggestionsCount,
    isLoading,
    approveSuggestions,
    rejectSuggestions,
  };
}
